import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, ChevronRight, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  return (
    <>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-background focus:text-foreground focus:outline-none focus:ring-2 focus:ring-primary rounded-md shadow-md transition-all">
        Skip to main content
      </a>
      <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm">
      {/* Promotional Banner */}
      {isBannerVisible && (
        <div className="bg-accent text-accent-foreground py-2 px-4 text-sm font-medium relative z-[60] flex items-center justify-center min-h-[40px]">
          <Link to="/specials" className="hover:underline flex items-center justify-center gap-1 text-center max-w-[90%] pr-6">
            <span className="truncate sm:whitespace-normal">🌟 New Patient Specials Available! Click here to claim your offer.</span>
            <ChevronRight className="h-4 w-4 shrink-0" />
          </Link>
          <button 
            onClick={() => setIsBannerVisible(false)}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-1.5 hover:bg-accent-foreground/20 rounded-full transition-colors flex items-center justify-center"
            aria-label="Dismiss promotional banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Desktop Header */}
      <div className="hidden xl:flex container mx-auto px-4 h-24 items-center justify-between">
        <Link to="/" className="shrink-0" aria-label="Woods Spine and Wellness Home" onClick={() => window.scrollTo(0, 0)}>
          <Logo className="h-16" />
        </Link>

        {/* Desktop Nav & Button Centered */}
        <div className="flex flex-1 items-center justify-center gap-6 lg:gap-8 px-4">
          <nav className="flex items-center gap-4 lg:gap-6">
            <Link to="/" className="text-sm font-medium hover:text-primary transition-colors">Home</Link>
            <div className="relative group">
              <Link to="/services" className="text-sm font-medium hover:text-primary transition-colors flex items-center gap-1">
                Services <ChevronDown className="h-4 w-4" />
              </Link>
              <div className="absolute top-full left-0 mt-2 w-64 bg-background border shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all flex flex-col p-2 z-50">
                <Link to="/longview-chirothin-weight-loss" className="p-2 hover:bg-muted rounded text-sm font-medium">ChiroThin Weight Loss</Link>
                <Link to="/longview-spinal-decompression" className="p-2 hover:bg-muted rounded text-sm font-medium">DRX9000 Spinal Decompression</Link>
                <Link to="/longview-red-light-therapy" className="p-2 hover:bg-muted rounded text-sm font-medium">Red Light Therapy</Link>
                <Link to="/longview-softwave-pain-management-tissue-regeneration" className="p-2 hover:bg-muted rounded text-sm font-medium">SoftWave TRT</Link>
              </div>
            </div>
            <div className="relative group">
              <Link to="/what-we-treat" className="text-sm font-medium hover:text-primary transition-colors flex items-center gap-1">
                What We Treat <ChevronDown className="h-4 w-4" />
              </Link>
              <div className="absolute top-full left-0 mt-2 w-64 bg-background border shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all flex flex-col p-2 z-50">
                <Link to="/what-we-treat#sciatica-leg-pain" className="p-2 hover:bg-muted rounded text-sm font-medium">Sciatica & Leg Pain</Link>
                <Link to="/what-we-treat#neuropathy" className="p-2 hover:bg-muted rounded text-sm font-medium">Neuropathy</Link>
                <Link to="/what-we-treat#plantar-fasciitis" className="p-2 hover:bg-muted rounded text-sm font-medium">Plantar Fasciitis</Link>
                <Link to="/what-we-treat#herniated-bulging-discs" className="p-2 hover:bg-muted rounded text-sm font-medium">Herniated & Bulging Discs</Link>
                <Link to="/what-we-treat#weight-management" className="p-2 hover:bg-muted rounded text-sm font-medium">Weight Management</Link>
                <Link to="/what-we-treat" className="p-2 hover:bg-muted rounded text-sm font-medium text-primary mt-1 border-t">View All Conditions</Link>
              </div>
            </div>
            <div className="relative group">
              <span className="text-sm font-medium hover:text-primary transition-colors flex items-center gap-1 cursor-pointer">
                Patients <ChevronDown className="h-4 w-4" />
              </span>
              <div className="absolute top-full left-0 mt-2 w-56 bg-background border shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all flex flex-col p-2 z-50">
                <a href="https://intake.helloinnate.com" target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-muted rounded text-sm font-medium text-accent">Online Paperwork</a>
                <Link to="/specials" className="p-2 hover:bg-muted rounded text-sm font-medium">Specials</Link>
                <Link to="/financials" className="p-2 hover:bg-muted rounded text-sm font-medium">Finance Options</Link>
              </div>
            </div>
            <div className="relative group">
              <Link to="/about" className="text-sm font-medium hover:text-primary transition-colors flex items-center gap-1">
                About <ChevronDown className="h-4 w-4" />
              </Link>
              <div className="absolute top-full left-0 mt-2 w-48 bg-background border shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all flex flex-col p-2 z-50">
                <Link to="/about" className="p-2 hover:bg-muted rounded text-sm font-medium">Our Team</Link>
                <Link to="/testimonials" className="p-2 hover:bg-muted rounded text-sm font-medium">Testimonials</Link>
                <Link to="/blog" className="p-2 hover:bg-muted rounded text-sm font-medium">Blog</Link>
              </div>
            </div>
            <Link to="/contact" className="text-sm font-medium hover:text-primary transition-colors">Contact</Link>
          </nav>
          
          <div className="flex items-center gap-2 shrink-0">
            <Button asChild className="bg-accent text-white hover:bg-accent/90">
              <Link to="/appointment-scheduling">Book Appointment</Link>
            </Button>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <a 
                  href="tel:+19032158828" 
                  className="w-10 h-10 rounded-full bg-white/10 border border-border flex items-center justify-center text-foreground hover:bg-accent hover:text-white hover:border-accent transition-colors shrink-0"
                  aria-label="Call (903) 215-8828"
                >
                  <Phone className="h-4 w-4" />
                </a>
              </TooltipTrigger>
              <TooltipContent>
                <p>(903) 215-8828</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Spacer to balance logo width and leave dedicated room for the floating translation widget */}
        <div className="w-[120px] lg:w-[150px] shrink-0"></div>
      </div>

      {/* Mobile Header (2 Rows) */}
      <div className="flex xl:hidden flex-col w-full">
        {/* Row 1: Logo & Language Spacer */}
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/" className="shrink-0" aria-label="Woods Spine and Wellness Home" onClick={() => { setIsMenuOpen(false); window.scrollTo(0, 0); }}>
            <Logo className="h-10" />
          </Link>
          <div className="w-[120px] shrink-0"></div> {/* Spacer for GTranslate */}
        </div>
        
        {/* Row 2: Book Appointment & Menu Toggle */}
        <div className="flex items-center justify-between px-4 h-14 border-t bg-muted/20">
          <div className="flex items-center gap-2">
            <Button asChild size="sm" className="bg-accent text-white hover:bg-accent/90">
              <Link to="/appointment-scheduling" onClick={() => setIsMenuOpen(false)}>Book Appointment</Link>
            </Button>
            <a 
              href="tel:+19032158828" 
              className="w-9 h-9 rounded-full bg-white/10 border border-border flex items-center justify-center text-foreground hover:bg-accent hover:text-white hover:border-accent transition-colors shrink-0"
              aria-label="Call (903) 215-8828"
            >
              <Phone className="h-4 w-4" />
            </a>
          </div>
          <button 
            className="p-2 text-foreground flex items-center gap-2 font-medium text-sm"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <><X className="h-5 w-5" /> Close</>
            ) : (
              <><Menu className="h-5 w-5" /> Menu</>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMenuOpen && (
        <div className="xl:hidden border-t p-4 bg-background max-h-[calc(100vh-7.5rem)] overflow-y-auto">
          <nav className="flex flex-col gap-4">
            <Link to="/" onClick={() => setIsMenuOpen(false)} className="text-sm font-medium">Home</Link>
            <div className="flex flex-col gap-2">
              <Link to="/services" onClick={() => setIsMenuOpen(false)} className="text-sm font-medium">Services</Link>
              <div className="flex flex-col gap-2 pl-4 border-l ml-2">
                <Link to="/longview-chirothin-weight-loss" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">ChiroThin Weight Loss</Link>
                <Link to="/longview-spinal-decompression" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">DRX9000 Spinal Decompression</Link>
                <Link to="/longview-red-light-therapy" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">Red Light Therapy</Link>
                <Link to="/longview-softwave-pain-management-tissue-regeneration" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">SoftWave TRT</Link>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Link to="/what-we-treat" onClick={() => setIsMenuOpen(false)} className="text-sm font-medium">What We Treat</Link>
              <div className="flex flex-col gap-2 pl-4 border-l ml-2">
                <Link to="/what-we-treat#sciatica-leg-pain" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">Sciatica & Leg Pain</Link>
                <Link to="/what-we-treat#neuropathy" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">Neuropathy</Link>
                <Link to="/what-we-treat#weight-management" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">Weight Management</Link>
                <Link to="/what-we-treat#herniated-bulging-discs" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">Herniated Discs</Link>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">Patients</span>
              <div className="flex flex-col gap-2 pl-4 border-l ml-2">
                <a href="https://intake.helloinnate.com" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)} className="text-sm font-medium text-accent">Online Paperwork</a>
                <Link to="/specials" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">Specials</Link>
                <Link to="/financials" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">Finance Options</Link>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Link to="/about" onClick={() => setIsMenuOpen(false)} className="text-sm font-medium">About</Link>
              <div className="flex flex-col gap-2 pl-4 border-l ml-2">
                <Link to="/about" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">Our Team</Link>
                <Link to="/testimonials" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">Testimonials</Link>
                <Link to="/blog" onClick={() => setIsMenuOpen(false)} className="text-sm text-muted-foreground hover:text-primary">Blog</Link>
              </div>
            </div>
            <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="text-sm font-medium">Contact</Link>
          </nav>
        </div>
      )}
    </header>
    </>
  );
}
